<template>
  <div class="animated fadeIn">
    <b-row>
      <PageTitle></PageTitle>
      <filter-area
        @filterData="filterData()"
        @resetData="resetFilerAreaData()"
      >
        <b-row>
          <b-col cols="6">
            <b-form-group>
              <label for="consentModel">{{$t('informedConsent.consentModels')}}</label>
              <v-select id="consentModel" v-model="filterArea.consentModel" :options="models" multiple  :getOptionLabel="getParsedModels"></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group>
              <label for="regions">{{$t('informedConsent.regionLabel')}}</label>
              <v-select id="regions" v-model="filterArea.region" index="code" :options="regions" multiple></v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <b-form-group>
              <label for="informationModel">{{$t('informedConsent.informationModels')}}</label>
              <v-select id="informationModel" v-model="filterArea.informationModel" :options="models" multiple :getOptionLabel="getParsedModels"></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="3">
              <b-form-group>
                <label for="validityStartDate">{{$t('informedConsent.validityFrom')}}</label>
                <datepicker v-model="filterArea.validityStartDate" name="validityStartDate" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
              </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group>
              <label for="validityEndDate">{{$tc('informedConsent.validityTo')}}</label>
              <datepicker v-model="filterArea.validityEndDate" name="validityEndDate" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
            </b-form-group>
          </b-col>
        </b-row>
      </filter-area>
      <b-col lg="12">
        <b-card>
          <b-table
            :stacked="stacked"
            :hover="hover"
            :striped="striped"
            :bordered="bordered"
            :small="small"
            :fixed="fixed"
            :items="items"
            :fields="captions"
            :current-page="1"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            :per-page="perPage"
            @sort-changed="sortingChanged"
            :no-sort-reset="true"
            :no-local-sorting="true">
            <template slot="validityStartDate" slot-scope="data">
              {{ formatDate(data.item.validityStartDate) }}
            </template>
            <template slot="validityEndDate" slot-scope="data">
              {{ formatDate(data.item.validityEndDate) }}
            </template>
            <template slot="actions" slot-scope="data">
              <div class="btn-group" role="group">
                <router-link
                  tag="button"
                  class="btn btn-primary fa fa-search"
                  v-b-tooltip.hover
                  :title="$t('actions.show')"
                  :to="`/informationConsents/show/${data.item.id}`"
                />
                <router-link
                  tag="button"
                  class="btn btn-warning fa fa-edit"
                  v-b-tooltip.hover
                  :title="$t('actions.edit')"
                  :to="`/informationConsents/edit/${data.item.id}`"
                />
              </div>
            </template>
          </b-table>
          <div class="row d-flex justify-content-between">
            <div class="col-sm-4">
              <b-pagination :total-rows="rowCount" :per-page="perPage" v-model="currentPage"
                            :prev-text="$t('actions.previous')"
                            :next-text="$t('actions.next')"
                            hide-goto-end-buttons @input="fetchData(currentPage)"/>
            </div>
          </div>
          <div slot="footer">
            <b-row>
              <b-col sm="12">
                <router-link
                  tag="button"
                  class="btn btn-block btn-primary float-right col-2"
                  role="button"
                  to="/informationConsents/new"
                >
                  {{ $t('actions.create') }}
                </router-link>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import PageTitle from '../../components/PageTitle'
import FilterArea from "../../components/FilterArea";
import i18n from '@/config/i18n'
import Datepicker from 'vuejs-datepicker';
import {it} from 'vuejs-datepicker/dist/locale'
import miniToastr from "mini-toastr";
import moment from "moment"
import InformationConsentService from "../../services/InformationConsentService";
import ModelService from "@/services/ModelService";
import RegionService from "@/services/RegionService";
const modelService = ModelService.build();
const regionService = RegionService.build();
const informationConsentService = InformationConsentService.build();
import vSelect from 'vue-select'

export default {
  name: "InformationConsentList",
  inheritAttrs: false,
  components: {
    PageTitle,
    Datepicker,
    vSelect,
    FilterArea,
  },
  props: {
    caption: {
      type: String,
      default: 'Informative consensi'
    },
    stacked: {
      type: String,
      default: 'sm'
    },
    hover: {
      type: Boolean,
      default: true
    },
    striped: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 7
    }
  },
  created() {
    miniToastr.init(); // config can be passed here miniToastr.init(config)
    miniToastr.setIcon('error', 'i', {'class': 'fas fa-exclamation-triangle'});
    this.fetchData(1);
    this.getModels();
    this.getRegions();
  },
  data: () => {
    return {
      fields: [
        {
          key: 'consentModelCode',
          sortable: true,
          label: i18n.t('informedConsent.consentModelCode'),
        },
        {
          key: 'consentModelDescription',
          sortable: true,
          label: i18n.t('informedConsent.consentModelDescription'),
        },
        {
          key: 'informationModelCode',
          sortable: true,
          label: i18n.t('informedConsent.informationModelCode'),
        },{
          key: 'informationModelDescription',
          sortable: true,
          label: i18n.t('informedConsent.informationModelDescription'),
        },{
          key: 'regionLabel',
          sortable: true,
          label: i18n.t('informedConsent.regionLabel'),
        },
        {
          key: 'validityStartDate',
          sortable: true,
          label: i18n.t('informedConsent.validityFromDate'),
        },
        {
          key: 'validityEndDate',
          sortable: false,
          label: i18n.t('informedConsent.validityEndDate')
        },
        {
          key: 'actions',
          sortable: false,
          label: 'Azioni'
        }
      ],
      datepickerLanguage: it,
      currentPage: 1,
      tableData: [],
      regions: [],
      models: [],
      tableTotalRows: 0,
      sortBy: 'code',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterArea: {
        consentModel: null,
        region: null,
        validityStartDate: null,
        validityEndDate: null,
        informationModel: null
      }
    }
  },
  computed: {
    items: function () {
      const items = this.tableData;
      return Array.isArray(items) ? items : []
    },
    captions: function () {
      return this.fields
    },
    rowCount: function () {
      return this.tableTotalRows
    },
    encodedConsentModelFilter: function () {
      return this.filterArea.consentModel.map((model) => model.code);
    },
    encodedInformationModelFilter: function () {
      return this.filterArea.informationModel.map((model) => model.code);
    }
  },
  methods: {
    formatDate (date) {
      if (!date)
        return null

      return moment(date).format('DD/MM/YYYY');
    },
    getParsedModels(model) {
      return `${model.description} (ID: ${model.code})`
    },
    async getModels() {
      this.models = (await modelService.search({ max: -1 })).data;
    },
    async getRegions() {
      this.regions = (await regionService.search({ max: -1 })).regions;
    },
    async fetchData(currentPage) {
      (
        {
          informationConsentModels: this.tableData = [],
          total: this.tableTotalRows = 0
        } = await informationConsentService.search(
          {
            max: this.perPage,
            offset: (currentPage - 1) * this.perPage,
            order: (this.sortDesc ? 'desc' : 'asc'),
            consentModelCode: this.filterArea.consentModel ? this.encodedConsentModelFilter : null,
            informationModelCode: this.filterArea.informationModel ? this.encodedInformationModelFilter : null,
            validityStartDateFrom: this.formatDate(this.filterArea.validityStartDate),
            validityStartDateTo: this.formatDate(this.filterArea.validityEndDate),
            regionCode: this.filterArea.region ? this.filterArea.region : null
          }
        )
      )
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchData(this.currentPage)
    },
    filterData(){
      this.fetchData(1)
    },
    resetFilerAreaData(){
      this.filterArea = {
        consentModel: null,
        region: null,
        validityStartDate: null,
        validityEndDate: null,
        informationModel: null,
      };
      this.fetchData(1)
    },
  }
}
</script>
